import { useState, useEffect } from 'react';
import { useViewport } from '../../../hooks/useViewport';

interface JobSearchProps {
  initialSearch?: string;
  onSearch: (search: string) => void;
  placeholder?: string;
}

export function JobSearch({ 
  initialSearch = '', 
  onSearch, 
  placeholder = 'Search jobs by title, company, or skills...' 
}: JobSearchProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [isFocused, setIsFocused] = useState(false);
  const { isMobile, prefersReducedMotion } = useViewport();

  // Shorter placeholder for mobile
  const mobilePlaceholder = 'Search jobs...';

  // Update local state when initialSearch changes
  useEffect(() => {
    setSearchTerm(initialSearch);
  }, [initialSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchTerm.trim());
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
      onSearch('');
    }
  };

  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-200';
  const hoverScaleClasses = prefersReducedMotion ? '' : 'transform hover:scale-105';

  return (
    <form onSubmit={handleSubmit} className="relative max-w-4xl mx-auto w-full">
      <div className={`relative flex items-center bg-white rounded-xl shadow-lg border-2 ${transitionClasses} ${
        isFocused ? 'border-primary-500 shadow-xl' : 'border-gray-200'
      }`}>
        {/* Search Icon */}
        <div className="absolute left-3 sm:left-4 flex items-center pointer-events-none">
          <svg 
            className={`w-5 h-5 ${transitionClasses} ${
              isFocused ? 'text-primary-500' : 'text-gray-400'
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
            />
          </svg>
        </div>

        {/* Search Input */}
        <input
          type="search"
          inputMode="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={isMobile ? mobilePlaceholder : placeholder}
          className="touch-target flex-1 pl-10 sm:pl-12 pr-16 sm:pr-20 py-3 sm:py-4 text-base sm:text-lg text-gray-900 bg-transparent border-none outline-none placeholder-gray-400"
        />

        {/* Clear Button */}
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="touch-target-sm absolute right-14 sm:right-16 p-2 text-gray-400 hover:text-gray-600"
            title="Clear search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Search Button */}
        <button
          type="submit"
          className={`touch-target absolute right-1.5 sm:right-2 px-4 sm:px-6 py-2 bg-primary-600 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${transitionClasses} ${hoverScaleClasses}`}
        >
          {isMobile ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          ) : (
            'Search'
          )}
        </button>
      </div>

      {/* Quick Search Suggestions - Scrollable on mobile */}
      <div className="mt-3 sm:mt-4 flex flex-nowrap sm:flex-wrap justify-start sm:justify-center gap-2 overflow-x-auto mobile-scroll-x pb-2 sm:pb-0">
        <span className="text-xs sm:text-sm text-slate-300 mr-1 sm:mr-2 flex-shrink-0">Popular:</span>
        {[
          'Faculty',
          'Professor',
          'Academic Officer',
          'Research',
          'Software Developer',
          'Remote',
        ].map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => {
              setSearchTerm(suggestion);
              onSearch(suggestion);
            }}
            className={`touch-target-sm flex-shrink-0 px-3 py-1 text-xs sm:text-sm bg-white/10 text-white rounded-full hover:bg-white/20 ${transitionClasses}`}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </form>
  );
}